import React, { useEffect, useState } from 'react';
import SEO from '../../components/SEO';
import PageLoader from '../../components/PageLoader';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';
import Pagination from '../../components/Pagination';
import { useSidebar } from '../../contexts/SidebarContext';
import { showSuccess, showError, showConfirm } from '../../utils/toast';

const AdminTagsContent = () => {
  const { isCollapsed } = useSidebar();
  const [tags, setTags] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({ name: '', metaTitle: '', metaDescription: '', active: true, featured: false });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('a-z');
  const [limit, setLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const ENTRIES_OPTIONS = [5, 10, 20, 50];

  useEffect(() => {
    fetchTags();
  }, [currentPage, search, sort, limit]);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit, sort };
      if (search) params.search = search;
      const response = await api.get('/admin/tags', { params });
      const newTags = response.data.tags;
      const newPagination = response.data.pagination;
      setTags(newTags);
      setPagination(newPagination);
      // If current page is empty after delete (e.g. was last item on page) and there are other pages, go to page 1
      if (newTags.length === 0 && currentPage > 1 && newPagination?.totalItems > 0) {
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTag) {
        await api.put(`/tags/${editingTag._id}`, formData);
      } else {
        await api.post('/tags', formData);
      }
      setShowModal(false);
      setEditingTag(null);
      setFormData({ name: '', metaTitle: '', metaDescription: '', active: true, featured: false });
      fetchTags();
      showSuccess(editingTag ? 'Tag updated successfully!' : 'Tag created successfully!');
    } catch (error) {
      showError(error.response?.data?.message || 'Error saving tag');
    }
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      metaTitle: tag.metaTitle || '',
      metaDescription: tag.metaDescription || '',
      active: tag.active,
      featured: tag.featured || false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    showConfirm(
      'Are you sure you want to delete this tag? This action cannot be undone.',
      async () => {
        try {
          await api.delete(`/tags/${id}`);
          fetchTags();
          showSuccess('Tag deleted successfully!');
        } catch (error) {
          showError(error.response?.data?.message || 'Error deleting tag');
        }
      }
    );
  };

  return (
    <>
      <SEO title="Manage Tags - Admin" description="Manage tags." noindex />

      <main className={`flex-1 transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'}`}>
        <div className="container-custom py-6 md:py-12">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-50">Manage Tags</h1>
            <button
              onClick={() => {
                setEditingTag(null);
                setFormData({ name: '', metaTitle: '', metaDescription: '', active: true, featured: false });
                setShowModal(true);
              }}
              className="btn-primary text-sm whitespace-nowrap"
            >
              <span className="hidden sm:inline">Add Tag</span>
              <span className="sm:hidden">+ Add</span>
            </button>
          </div>

          {/* Search, Sort, and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex flex-1 flex-wrap gap-2">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput); setCurrentPage(1); } }}
                placeholder="Search by name or slug..."
                className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={() => { setSearch(searchInput); setCurrentPage(1); }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
              >
                Search
              </button>
              {(searchInput || search) && (
                <button
                  onClick={() => { setSearchInput(''); setSearch(''); setCurrentPage(1); }}
                  className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label htmlFor="sort-tags" className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Sort:</label>
                <select
                  id="sort-tags"
                  value={sort}
                  onChange={(e) => { setSort(e.target.value); setCurrentPage(1); }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                >
                  <option value="a-z">A to Z</option>
                  <option value="z-a">Z to A</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="limit-tags" className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Entries per page:</label>
                <select
                  id="limit-tags"
                  value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setCurrentPage(1); }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                >
                  {ENTRIES_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <PageLoader message="Loading tags..." />
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-14">Sr. No.</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Slug</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Businesses</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Featured</th>
                        <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {tags.map((tag, index) => {
                        const srNo = (currentPage - 1) * limit + index + 1;
                        return (
                          <tr key={tag._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-400 text-sm">{srNo}</td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap font-semibold text-gray-900 dark:text-gray-50">{tag.name}</td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400 text-sm">{tag.slug}</td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-300 text-sm">{tag.businessCount ?? 0}</td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <span className={`badge text-xs ${tag.active ? 'badge-success' : 'badge-error'}`}>
                                {tag.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <span className={`badge text-xs ${tag.featured ? 'badge-warning' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                                {tag.featured ? '⭐ Featured' : 'Not Featured'}
                              </span>
                            </td>
                            <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                              <div className="flex gap-3">
                                <button
                                  onClick={() => handleEdit(tag)}
                                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium transition-colors"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(tag._id)}
                                  className="text-error-600 dark:text-error-400 hover:text-error-700 dark:hover:text-error-300 text-sm font-medium transition-colors"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {tags.map((tag, index) => {
                  const srNo = (currentPage - 1) * limit + index + 1;
                  return (
                    <div key={tag._id} className="card p-4 md:p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{srNo}.</span>
                            <h3 className="font-bold text-lg text-gray-900 dark:text-gray-50">{tag.name}</h3>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{tag.slug}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                            {(tag.businessCount ?? 0)} business{(tag.businessCount ?? 0) !== 1 ? 'es' : ''}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <span className={`badge text-xs ${tag.active ? 'badge-success' : 'badge-error'}`}>
                              {tag.active ? 'Active' : 'Inactive'}
                            </span>
                            <span className={`badge text-xs ${tag.featured ? 'badge-warning' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                              {tag.featured ? '⭐ Featured' : 'Not Featured'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => handleEdit(tag)}
                          className="btn-outline text-sm py-1.5 px-3 flex-1"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(tag._id)}
                          className="btn-outline text-sm py-1.5 px-3 flex-1 text-error-600 dark:text-error-400 border-error-600 dark:border-error-400"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {tags.length === 0 && (
                <div className="card p-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-lg">
                    {search ? 'No tags match your search.' : 'No tags found.'}
                  </p>
                </div>
              )}

              {pagination && pagination.totalPages >= 1 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    itemsPerPage={pagination.itemsPerPage}
                    itemsOnCurrentPage={pagination.itemsOnCurrentPage}
                    onPageChange={(page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  />
                </div>
              )}
            </>
          )}

          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4 animate-fade-in">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 md:p-8 w-full max-w-md animate-scale-in">
                <h2 className="text-xl md:text-2xl font-bold mb-4 text-gray-800 dark:text-gray-50">
                  {editingTag ? 'Edit Tag' : 'Add Tag'}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Meta Title (SEO)</label>
                    <input
                      type="text"
                      value={formData.metaTitle}
                      onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
                      placeholder="Custom page title for SEO"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 dark:text-gray-300 font-semibold mb-2">Meta Description (SEO)</label>
                    <textarea
                      value={formData.metaDescription}
                      onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                      rows="2"
                      placeholder="Custom meta description for SEO"
                      className="input-field"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label className="ml-2 text-gray-700 dark:text-gray-300">Active</label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <label className="ml-2 text-gray-700 dark:text-gray-300">⭐ Featured (Show on Home Page)</label>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <button
                      type="submit"
                      className="btn-primary flex-1"
                    >
                      {editingTag ? 'Update' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingTag(null);
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

const AdminTags = () => {
  return (
    <AdminLayout>
      <AdminTagsContent />
    </AdminLayout>
  );
};

export default AdminTags;

